import { ARTICLE_CALLOUT_ATTRIBUTE, articleCalloutKindOf, articleCalloutWordKey, t, type ArticleCalloutKind } from '@bms2/shared';

/**
 * 🔒 The article editor's callouts (T3572; decision 10530): a Note, Warning or
 * Tip block, stored as an `aside` carrying `data-kb-callout`, holding the
 * paragraphs and lists the selection covered. The style menu wraps, changes and
 * lifts one through these; the drawing is the one stylesheet rule the reader
 * shares, and nothing here writes the glyph or the word into the words.
 */

/** The callout an element stands in, inside the surface, or null. */
export function calloutAt(surface: HTMLElement, from: Node | null): { aside: HTMLElement; kind: ArticleCalloutKind } | null {
  const element = from instanceof Element ? from : from?.parentElement ?? null;
  const aside = element?.closest<HTMLElement>(`aside[${ARTICLE_CALLOUT_ATTRIBUTE}]`) ?? null;
  if (!aside || !surface.contains(aside)) return null;
  const kind = articleCalloutKindOf(aside.getAttribute(ARTICLE_CALLOUT_ATTRIBUTE) ?? '');
  return kind ? { aside, kind } : null;
}

/**
 * The surface's own children a range covers, in order: what a callout wraps.
 * A collapsed caret covers the child it stands in. Null where words stand
 * loose in the surface, outside any block, so the caller makes them a
 * paragraph first.
 */
export function coveredTopBlocks(surface: HTMLElement, range: Range): HTMLElement[] | null {
  const children = Array.from(surface.childNodes);
  const touched = range.collapsed
    ? children.filter((child) => child === range.startContainer || child.contains(range.startContainer))
    : children.filter((child) => range.intersectsNode(child));
  if (touched.length === 0 && range.collapsed && range.startContainer === surface) {
    const at = children[Math.min(range.startOffset, children.length - 1)];
    if (at) touched.push(at);
  }
  if (touched.some((child) => !(child instanceof HTMLElement) && (child.textContent ?? '').trim() !== '')) return null;
  return touched.filter((child): child is HTMLElement => child instanceof HTMLElement);
}

/** 🔨 The blocks a callout never holds (decision 10530): a table, a picture, a rule, the contents, another callout. */
const REFUSED = `table, img, hr, nav, aside`;

/** Whether a callout over these blocks is refused, because one is or holds what a callout never holds. */
export function calloutRefused(blocks: readonly HTMLElement[]): boolean {
  return blocks.some((block) => block.matches(REFUSED) || block.querySelector(REFUSED) !== null);
}

/** A block's children moved into a fresh paragraph, which takes the block's place. */
function asParagraph(block: HTMLElement): HTMLElement {
  const paragraph = document.createElement('p');
  paragraph.append(...Array.from(block.childNodes));
  if (block.getAttribute('style')) paragraph.setAttribute('style', block.getAttribute('style')!);
  block.replaceWith(paragraph);
  return paragraph;
}

/**
 * 🔒 The blocks wrapped in one callout of the kind (decision 10530): a heading
 * or a code block becomes a paragraph, and a quotation is lifted into its own
 * paragraphs first, so the callout holds paragraphs and lists alone.
 */
export function wrapInCallout(blocks: readonly HTMLElement[], kind: ArticleCalloutKind): HTMLElement | null {
  const held: HTMLElement[] = [];
  for (const block of blocks) {
    if (/^(H[1-6]|PRE)$/.test(block.tagName)) held.push(asParagraph(block));
    else if (block.tagName === 'BLOCKQUOTE') {
      const inner = Array.from(block.children).filter((child): child is HTMLElement => child instanceof HTMLElement && /^(P|DIV|UL|OL)$/.test(child.tagName));
      if (inner.length > 0 && inner.length === block.children.length) {
        block.replaceWith(...Array.from(block.childNodes));
        held.push(...inner);
      } else held.push(asParagraph(block));
    } else held.push(block);
  }
  const first = held[0];
  if (!first) return null;
  const aside = document.createElement('aside');
  aside.setAttribute(ARTICLE_CALLOUT_ATTRIBUTE, kind);
  first.before(aside);
  aside.append(...held);
  return aside;
}

/** 🔒 A callout's words taken back out (decision 10530): its blocks stand where it stood, and words it held bare become one paragraph. */
export function liftCallout(aside: HTMLElement): void {
  if (Array.from(aside.children).some((child) => /^(P|DIV|UL|OL|H[1-6]|PRE|BLOCKQUOTE)$/.test(child.tagName))) {
    aside.replaceWith(...Array.from(aside.childNodes));
    return;
  }
  asParagraph(aside);
}

/** Whether a block holds no words and nothing drawn: a line the caret stands on with nothing typed. */
function emptyLine(block: Element): boolean {
  return (block.textContent ?? '').replace(/\u200b/g, '').trim() === '' && block.querySelector('img, table, hr') === null;
}

/**
 * 🔒 Enter on an empty last line leaves the callout downward, and Enter on an
 * empty first line leaves it upward (decision 10530; the T3572 review), so a
 * callout opening the article still has a line above it to write on. The line
 * moves out after or before it, and a callout left holding nothing goes.
 * Answers the line the caret moves to, or null where the caret is not on such
 * a line.
 */
export function leaveCallout(surface: HTMLElement, from: Node | null): HTMLElement | null {
  const callout = calloutAt(surface, from);
  if (!callout) return null;
  const element = from instanceof Element ? from : from?.parentElement ?? null;
  const line = element?.closest<HTMLElement>('p, div');
  if (!line || line === callout.aside || line.parentElement !== callout.aside || !emptyLine(line)) return null;
  if (callout.aside.lastElementChild === line) callout.aside.after(line);
  else if (callout.aside.firstElementChild === line) callout.aside.before(line);
  else return null;
  if (callout.aside.childNodes.length === 0 || emptyLine(callout.aside)) callout.aside.remove();
  return line;
}

/**
 * 🔒 A picture, a table or a rule inserted with the caret in a callout lands
 * on a new line after it (decision 10530): the empty line is made here and the
 * caller puts the caret in it before the insert. Null where the caret stands in
 * no callout.
 */
export function lineAfterCallout(surface: HTMLElement, from: Node | null): HTMLElement | null {
  const callout = calloutAt(surface, from);
  if (!callout) return null;
  const line = document.createElement('p');
  line.append(document.createElement('br'));
  callout.aside.after(line);
  return line;
}

/**
 * 🔒 Each callout named for a screen reader by its word (decision 10530; the
 * T3572 review): the drawn word is generated content, so the `aside` carries
 * the same word from the strings as its `aria-label`, in the editor and the
 * reader alike, and the stylesheet draws that label as the callout's word (T3596). The label is drawn, never stored: the editor strips it from
 * what it hands up, and the allowed table keeps no such attribute.
 */
export function labelCallouts(root: ParentNode): void {
  for (const aside of Array.from(root.querySelectorAll<HTMLElement>(`aside[${ARTICLE_CALLOUT_ATTRIBUTE}]`))) {
    const kind = articleCalloutKindOf(aside.getAttribute(ARTICLE_CALLOUT_ATTRIBUTE) ?? '');
    const word = kind ? t(articleCalloutWordKey(kind)) : null;
    if (word && aside.getAttribute('aria-label') !== word) aside.setAttribute('aria-label', word);
  }
}

/** The markup with every drawn callout label taken out, which is what the surface hands its consumer. */
export function withoutCalloutLabels(html: string): string {
  return html.replace(/(<aside data-kb-callout="[a-z]+") aria-label="[^"]*"/g, '$1');
}
